import { forwardRef, useEffect, useState } from 'react';
import { string, array, arrayOf, bool, element, instanceOf, number, object, oneOfType } from 'prop-types';
import Picker from 'react-day-picker';
import Caption from './Caption';
import Navbar from './Navbar';
import DayElement from './Day';
import Styled, { DatepickerHeaderMobile, Weekday, WeekdayHeader } from './Styled';
import { defaultFromMonth, defaultToMonth, disabledDatesBase, formatDateToString } from '../index';
import { WEEKDAYS_LONG, WEEKDAYS_SHORT } from '../utils';
const preparedWeek = days => {
  const [lastDay, ...otherDays] = days;
  return otherDays.concat(lastDay);
};
const weekWithFirstDayMonday = preparedWeek(WEEKDAYS_SHORT);
const DayPicker = /*#__PURE__*/forwardRef(({
  isRange = false,
  isMobile = false,
  isShownControls = false,
  isTicketSearch = false,
  firstDayOfWeek = 1,
  numberOfMonths = 1,
  selectedDays,
  fromMonth = defaultFromMonth,
  toMonth = defaultToMonth,
  initialMonth = new Date(),
  disabledDays = [],
  allowDates = [],
  modifiers = {},
  captionElement = Caption,
  navbarElement = Navbar,
  dayElement = DayElement,
  AttentionComponent = null,
  OverlayComponent = null,
  isSelectedDatepickerRange = false,
  isContainer = false,
  onCloseModal = Function.prototype,
  ...props
}, ref) => {
  const [isShownAttention, setIsShownAttention] = useState(isMobile);
  const preparedDisabledDatesBase = disabledDatesBase.map(day => formatDateToString(day));
  const preparedDisabledDates = disabledDays.map(day => formatDateToString(day));
  const preparedDisabledDatesAll = [...preparedDisabledDatesBase, ...preparedDisabledDates];
  const mergedDisabledDays = date => {
    const preparedDate = formatDateToString(date);
    if (preparedDisabledDatesAll.includes(preparedDate)) {
      return date;
    }
    if (allowDates.length && !allowDates.includes(preparedDate)) {
      return date;
    }
    return null;
  };
  const modifiersWeekendDays = {
    weekend: {
      daysOfWeek: [0, 6]
    },
    resetOverlay: {
      daysOfWeek: [1, 0]
    }
  };
  const preparedModifiers = {
    ...modifiers,
    ...modifiersWeekendDays
  };
  const isHasDateTo = isRange && selectedDays[1].to;
  const setAttentionByDate = date => {
    const currentMonth = numberOfMonths === 2 ? date.getMonth() + 1 : date.getMonth();
    const maxMonth = toMonth.getMonth();
    if (currentMonth === maxMonth) {
      setIsShownAttention(true);
    } else {
      setIsShownAttention(false);
    }
  };
  const onMonthChange = date => isTicketSearch && setAttentionByDate(date);
  useEffect(() => {
    if (!isMobile && isTicketSearch) {
      setAttentionByDate(initialMonth);
    }
  }, []);
  const isOverlayShow = OverlayComponent && !isSelectedDatepickerRange;
  return /*#__PURE__*/React.createElement(Styled, {
    isRange: isRange,
    isShownControls: isShownControls,
    isHasDateTo: isHasDateTo,
    isContainer: isContainer
  }, isMobile && /*#__PURE__*/React.createElement(DatepickerHeaderMobile, null, isOverlayShow && /*#__PURE__*/React.createElement(OverlayComponent, {
    onCloseModal: onCloseModal
  }), /*#__PURE__*/React.createElement(WeekdayHeader, null, weekWithFirstDayMonday.map((day, idx) =>
  // eslint-disable-next-line react/no-array-index-key
  /*#__PURE__*/
  React.createElement(Weekday, {
    key: `weekday-${idx}`
  }, day)))), /*#__PURE__*/React.createElement(Picker, {
    weekdaysLong: WEEKDAYS_LONG,
    weekdaysShort: WEEKDAYS_SHORT,
    firstDayOfWeek: firstDayOfWeek,
    selectedDays: selectedDays,
    initialMonth: initialMonth,
    disabledDays: mergedDisabledDays,
    fromMonth: fromMonth,
    toMonth: toMonth,
    numberOfMonths: numberOfMonths,
    ref: ref,
    modifiers: preparedModifiers,
    captionElement: captionElement,
    navbarElement: navbarElement,
    renderDay: dayElement,
    onMonthChange: onMonthChange,
    ...props
  }), isShownAttention && isTicketSearch && /*#__PURE__*/React.createElement(AttentionComponent, {
    isOverlayShow: isOverlayShow
  }));
});
DayPicker.propTypes = {
  disabledDays: arrayOf(oneOfType([instanceOf(Date), string])),
  allowDates: array,
  fromMonth: instanceOf(Date),
  toMonth: instanceOf(Date),
  initialMonth: instanceOf(Date),
  isRange: bool,
  isMobile: bool,
  isShownControls: bool,
  isTicketSearch: bool,
  firstDayOfWeek: number,
  numberOfMonths: number,
  modifiers: object,
  captionElement: element,
  navbarElement: element,
  attentionElement: element,
  dayElement: element,
  isContainer: bool
};
export default DayPicker;